import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { DEMO_MODE_KEY } from '@/data/demoData'

interface DemoModeContextType {
  isDemoMode: boolean
  enableDemoMode: () => void
  disableDemoMode: () => void
  toggleDemoMode: () => void
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined)

interface DemoModeProviderProps {
  children: ReactNode
}

export function DemoModeProvider({ children }: DemoModeProviderProps) {
  const [isDemoMode, setIsDemoMode] = useState(false)

  useEffect(() => {
    // Check localStorage on mount
    const storedValue = localStorage.getItem(DEMO_MODE_KEY)
    setIsDemoMode(storedValue === 'true')
  }, [])

  const enableDemoMode = () => {
    localStorage.setItem(DEMO_MODE_KEY, 'true')
    setIsDemoMode(true)
  }

  const disableDemoMode = () => {
    localStorage.removeItem(DEMO_MODE_KEY)
    setIsDemoMode(false)
  }

  const toggleDemoMode = () => {
    if (isDemoMode) {
      disableDemoMode()
    } else {
      enableDemoMode()
    }
  }

  return (
    <DemoModeContext.Provider value={{ isDemoMode, enableDemoMode, disableDemoMode, toggleDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  )
}

export function useDemoMode() {
  const context = useContext(DemoModeContext)
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider')
  }
  return context
}
